import dayjs from '@/helpers/dayjs';
import {
  yupAddressPostcode,
  yupAddress,
  yupBoolean,
  yupMixed,
  yupNumber,
  yupObject,
  yupPeriodicValue,
  yupString,
} from '../../yup';
import {
  DepositType,
  PropertyDetailOption,
  PropertyType,
  PropertyPurpose,
  SaleStatus,
  StampDutyStatus,
  residentialCommercialTypes,
} from '@/helpers/const';
import { useFeatureFlags } from '@/helpers/useFeatureFlags';

import type {
  DepositTypeEnum,
  PropertyDetailEntry,
  PropertyDetailOptionsEnum,
  PropertyTypeEnum,
  SaleStatusEnum,
  StampDutyStatusEnum,
} from '@/types';

// Property types that require single security loan (cannot use Property to Purchase)
const singleSecurityRequired = [PropertyType.RETIREMENT_LAND_OR_COMMUNITY_HOME, PropertyType.VACANT_LAND];

// Helper to check if property purchase is restricted
const isPropertyPurchaseRestricted = (propertyOptions: string, propertyType: string) =>
  propertyOptions === PropertyDetailOption.PROPERTY_TO_PURCHASE &&
  (singleSecurityRequired as readonly string[]).includes(propertyType);

const { isNegativeGearingFy27Enabled } = useFeatureFlags();

const isNewBuildQuestionApplicable = (propertyOptions: string, propertyType: string, propertyPurpose: string) => {
  if (!isNegativeGearingFy27Enabled() || propertyPurpose !== PropertyPurpose.INVESTMENT_PROPERTY) {
    return false;
  }
  const isPurchase =
    propertyOptions === PropertyDetailOption.PROPERTY_TO_PURCHASE &&
    !isPropertyPurchaseRestricted(propertyOptions, propertyType);
  return isPurchase || propertyOptions === PropertyDetailOption.SUPPORTING_PROPERTY;
};

export const brokerPropertyDetailsSchema = yupObject<PropertyDetailEntry>({
  // Common fields
  propertyType: yupMixed<PropertyTypeEnum>().required().label('Property type'),
  propertyOptions: yupMixed<PropertyDetailOptionsEnum>().required().label('Property purpose'),

  address: yupAddress()
    .when(['propertyOptions', 'propertyType'], ([propertyOptions, propertyType], schema) =>
      isPropertyPurchaseRestricted(propertyOptions, propertyType) ? schema : schema.required(),
    )
    .label('Property address'),

  propertyPurpose: yupString()
    .when(
      ['propertyOptions', 'propertyType', 'commercialType'],
      ([propertyOptions, propertyType, commercialType], schema) => {
        if (isPropertyPurchaseRestricted(propertyOptions, propertyType)) {
          return schema;
        }
        // Not required if residential commercial type (auto-set)
        if (
          propertyType === PropertyType.COMMERCIAL &&
          residentialCommercialTypes.includes(commercialType as (typeof residentialCommercialTypes)[number])
        ) {
          return schema;
        }
        return schema.required();
      },
    )
    .label('Property purpose'),

  commercialType: yupString()
    .when('propertyType', {
      is: PropertyType.COMMERCIAL,
      then: (schema) => schema.required(),
    })
    .label('Commercial type'),

  idsType: yupString()
    .when(['propertyType', 'commercialType'], ([propertyType, commercialType], schema) => {
      const isResidentialCommercial =
        propertyType === PropertyType.COMMERCIAL &&
        residentialCommercialTypes.includes(commercialType as (typeof residentialCommercialTypes)[number]);
      return isResidentialCommercial ? schema.required() : schema;
    })
    .label('Residence type'),

  propertyLeased: yupBoolean()
    .nullable()
    .when(
      ['propertyOptions', 'propertyType', 'propertyPurpose'],
      ([propertyOptions, propertyType, propertyPurpose], schema) => {
        if (isPropertyPurchaseRestricted(propertyOptions, propertyType)) {
          return schema;
        }
        return propertyPurpose === PropertyPurpose.INVESTMENT_PROPERTY ? schema.required() : schema;
      },
    )
    .label('Property leased status'),

  // Negative Gearing (FY27) fields — only enforced when the feature flag is enabled
  isNewBuild: yupBoolean()
    .when(
      ['propertyOptions', 'propertyType', 'propertyPurpose'],
      ([propertyOptions, propertyType, propertyPurpose], schema) =>
        isNewBuildQuestionApplicable(propertyOptions, propertyType, propertyPurpose) ? schema.required() : schema,
    )
    .label('New build response'),

  isContractExchanged: yupBoolean()
    .when(
      ['propertyOptions', 'propertyType', 'propertyPurpose', 'isNewBuild', 'contractForSale'],
      ([propertyOptions, propertyType, propertyPurpose, isNewBuild, contractForSale], schema) => {
        // Only relevant when the new-build question applies and the answer is "No"
        if (!isNewBuildQuestionApplicable(propertyOptions, propertyType, propertyPurpose) || isNewBuild !== false) {
          return schema;
        }
        // Incoming/purchase: only required when there is a contract of sale; supporting: always required
        if (propertyOptions === PropertyDetailOption.PROPERTY_TO_PURCHASE) {
          return contractForSale === true ? schema.required() : schema;
        }
        return schema.required();
      },
    )
    .label('Contract exchange date response'),

  // PROPERTY_TO_SELL fields
  outstandingLoanAmount: yupNumber()
    .when('propertyOptions', {
      is: (val: string) =>
        val === PropertyDetailOption.PROPERTY_TO_SELL || val === PropertyDetailOption.SUPPORTING_PROPERTY,
      then: (schema) => schema.required(),
    })
    .label('Outstanding mortgage'),

  saleStatus: yupMixed<SaleStatusEnum>()
    .oneOf(Object.values(SaleStatus))
    .when('propertyOptions', {
      is: PropertyDetailOption.PROPERTY_TO_SELL,
      then: (schema) => schema.required(),
    })
    .label('Property sold response'),

  saleAmount: yupNumber()
    .when('propertyOptions', {
      is: PropertyDetailOption.PROPERTY_TO_SELL,
      then: (schema) => schema.required(),
    })
    .label('Sale price'),

  depositInTrust: yupBoolean()
    .when(['propertyOptions', 'saleStatus'], ([propertyOptions, saleStatus], schema) =>
      propertyOptions === PropertyDetailOption.PROPERTY_TO_SELL && saleStatus === SaleStatus.SOLD
        ? schema.required()
        : schema,
    )
    .label('Deposit held in trust response'),

  // PROPERTY_TO_PURCHASE fields
  contractForSale: yupBoolean()
    .when(['propertyOptions', 'propertyType'], ([propertyOptions, propertyType], schema) =>
      propertyOptions === PropertyDetailOption.PROPERTY_TO_PURCHASE &&
      !isPropertyPurchaseRestricted(propertyOptions, propertyType)
        ? schema.required()
        : schema,
    )
    .label('Contract for sale response'),

  receiveRentalIncome: yupBoolean()
    .when(
      ['propertyOptions', 'propertyType', 'propertyPurpose'],
      ([propertyOptions, propertyType, propertyPurpose], schema) => {
        if (
          propertyOptions === PropertyDetailOption.PROPERTY_TO_PURCHASE &&
          !isPropertyPurchaseRestricted(propertyOptions, propertyType) &&
          propertyPurpose !== 'owner_occupied'
        ) {
          return schema.required();
        }
        if (
          propertyOptions === PropertyDetailOption.SUPPORTING_PROPERTY &&
          propertyPurpose === PropertyPurpose.INVESTMENT_PROPERTY
        ) {
          return schema.required();
        }
        return schema;
      },
    )
    .label('Rental income response'),

  rentalIncome: yupPeriodicValue(yupNumber().required())
    .when('receiveRentalIncome', {
      is: true,
      then: (schema) => schema.required(),
    })
    .label('Expected rental income'),

  purchaseAmount: yupNumber()
    .when(['propertyOptions', 'propertyType'], ([propertyOptions, propertyType], schema) =>
      propertyOptions === PropertyDetailOption.PROPERTY_TO_PURCHASE &&
      !isPropertyPurchaseRestricted(propertyOptions, propertyType)
        ? schema.required()
        : schema,
    )
    .label('Purchase price'),

  depositType: yupMixed<DepositTypeEnum>()
    .oneOf(Object.values(DepositType))
    .when(['propertyOptions', 'propertyType'], ([propertyOptions, propertyType], schema) =>
      propertyOptions === PropertyDetailOption.PROPERTY_TO_PURCHASE &&
      !isPropertyPurchaseRestricted(propertyOptions, propertyType)
        ? schema.required()
        : schema,
    )
    .label('Deposit payment method'),

  stampDutyStatus: yupMixed<StampDutyStatusEnum>()
    .when(
      ['propertyOptions', 'propertyType', 'contractForSale'],
      ([propertyOptions, propertyType, contractForSale], schema) => {
        if (
          propertyOptions === PropertyDetailOption.PROPERTY_TO_PURCHASE &&
          !isPropertyPurchaseRestricted(propertyOptions, propertyType)
        ) {
          const baseSchema = schema.required().oneOf(Object.values(StampDutyStatus));
          // already_paid is only valid if contract for sale exists
          return contractForSale
            ? baseSchema
            : baseSchema.oneOf(Object.values(StampDutyStatus).filter((item) => item !== StampDutyStatus.ALREADY_PAID));
        }
        return schema;
      },
    )
    .label('Stamp duty response'),

  transferFeeCovered: yupBoolean()
    .when(['propertyOptions', 'propertyType'], ([propertyOptions, propertyType], schema) =>
      propertyOptions === PropertyDetailOption.PROPERTY_TO_PURCHASE &&
      !isPropertyPurchaseRestricted(propertyOptions, propertyType)
        ? schema.required()
        : schema,
    )
    .label('Transfer fee'),

  // Shared field for PROPERTY_TO_SELL and PROPERTY_TO_PURCHASE
  settlementDate: yupString()
    .when(['propertyOptions', 'propertyType', 'saleStatus'], ([propertyOptions, propertyType, saleStatus], schema) => {
      // PROPERTY_TO_SELL: required only when sold
      if (propertyOptions === PropertyDetailOption.PROPERTY_TO_SELL && saleStatus === SaleStatus.SOLD) {
        return schema
          .required()
          .isoDate()
          .test('validDate', '${path} must be within 12 months', (value: string) => {
            const date = dayjs(value);
            return !(date.isBefore(dayjs(), 'day') || date.isAfter(dayjs().add(1, 'year'), 'day'));
          });
      }
      // PROPERTY_TO_PURCHASE: required unless restricted
      if (
        propertyOptions === PropertyDetailOption.PROPERTY_TO_PURCHASE &&
        !isPropertyPurchaseRestricted(propertyOptions, propertyType)
      ) {
        return schema
          .required()
          .isoDate()
          .test('validDate', '${path} must be within 12 months', (value) => {
            const date = dayjs(value);
            return date.isAfter(dayjs(), 'day') && date.isBefore(dayjs().add(1, 'year'), 'day');
          });
      }
      return schema;
    })
    .label('Settlement date'),

  // Shared depositAmount field
  depositAmount: yupNumber()
    .when(
      ['propertyOptions', 'propertyType', 'saleStatus', 'saleAmount', 'purchaseAmount'],
      ([propertyOptions, propertyType, saleStatus, saleAmount, purchaseAmount], schema) => {
        // PROPERTY_TO_SELL: required when sold, max is saleAmount
        if (propertyOptions === PropertyDetailOption.PROPERTY_TO_SELL && saleStatus === SaleStatus.SOLD) {
          return schema.max(saleAmount, 'The deposit cannot be more than the sale price').required();
        }
        // PROPERTY_TO_PURCHASE: required unless restricted, max is purchaseAmount
        if (
          propertyOptions === PropertyDetailOption.PROPERTY_TO_PURCHASE &&
          !isPropertyPurchaseRestricted(propertyOptions, propertyType)
        ) {
          return schema.max(purchaseAmount, 'The deposit cannot be more than the purchase price').required();
        }
        return schema;
      },
    )
    .label('Deposit amount'),

  // SUPPORTING_PROPERTY fields
  supportingPropertyValue: yupNumber()
    .when('propertyOptions', {
      is: PropertyDetailOption.SUPPORTING_PROPERTY,
      then: (schema) => schema.required(),
    })
    .label('Estimated valuation'),
});

export const brokerPropertyDetailsSimpleSchema = yupObject<PropertyDetailEntry>({
  // Common fields
  propertyType: yupMixed<PropertyTypeEnum>().required().label('Property type'),
  propertyOptions: yupMixed<PropertyDetailOptionsEnum>().required().label('Property purpose'),

  address: yupAddressPostcode()
    .when(['propertyOptions', 'propertyType'], ([propertyOptions, propertyType], schema) =>
      isPropertyPurchaseRestricted(propertyOptions, propertyType) ? schema : schema.required(),
    )
    .label('Property address'),

  propertyPurpose: yupString()
    .when(
      ['propertyOptions', 'propertyType', 'commercialType'],
      ([propertyOptions, propertyType, commercialType], schema) => {
        if (isPropertyPurchaseRestricted(propertyOptions, propertyType)) {
          return schema;
        }
        // Not required if residential commercial type (auto-set)
        if (
          propertyType === PropertyType.COMMERCIAL &&
          residentialCommercialTypes.includes(commercialType as (typeof residentialCommercialTypes)[number])
        ) {
          return schema;
        }
        return schema.required();
      },
    )
    .label('Property purpose'),

  commercialType: yupString()
    .when('propertyType', {
      is: PropertyType.COMMERCIAL,
      then: (schema) => schema.required(),
    })
    .label('Commercial type'),

  idsType: yupString()
    .when(['propertyType', 'commercialType'], ([propertyType, commercialType], schema) => {
      const isResidentialCommercial =
        propertyType === PropertyType.COMMERCIAL &&
        residentialCommercialTypes.includes(commercialType as (typeof residentialCommercialTypes)[number]);
      return isResidentialCommercial ? schema.required() : schema;
    })
    .label('Residence type'),

  propertyLeased: yupBoolean()
    .when(
      ['propertyOptions', 'propertyType', 'propertyPurpose'],
      ([propertyOptions, propertyType, propertyPurpose], schema) => {
        if (isPropertyPurchaseRestricted(propertyOptions, propertyType)) {
          return schema;
        }
        return propertyPurpose === PropertyPurpose.INVESTMENT_PROPERTY ? schema.required() : schema;
      },
    )
    .label('Property leased status'),

  // Negative Gearing (FY27) fields — only enforced when the feature flag is enabled
  isNewBuild: yupBoolean()
    .when(
      ['propertyOptions', 'propertyType', 'propertyPurpose'],
      ([propertyOptions, propertyType, propertyPurpose], schema) =>
        isNewBuildQuestionApplicable(propertyOptions, propertyType, propertyPurpose) ? schema.required() : schema,
    )
    .label('New build response'),

  isContractExchanged: yupBoolean()
    .when(
      ['propertyOptions', 'propertyType', 'propertyPurpose', 'isNewBuild', 'contractForSale'],
      ([propertyOptions, propertyType, propertyPurpose, isNewBuild, contractForSale], schema) => {
        // Only relevant when the new-build question applies and the answer is "No"
        if (!isNewBuildQuestionApplicable(propertyOptions, propertyType, propertyPurpose) || isNewBuild !== false) {
          return schema;
        }
        // Incoming/purchase: only required when there is a contract of sale; supporting: always required
        if (propertyOptions === PropertyDetailOption.PROPERTY_TO_PURCHASE) {
          return contractForSale === true ? schema.required() : schema;
        }
        return schema.required();
      },
    )
    .label('Contract exchange date response'),

  // PROPERTY_TO_SELL fields
  outstandingLoanAmount: yupNumber()
    .when('propertyOptions', {
      is: (val: string) =>
        val === PropertyDetailOption.PROPERTY_TO_SELL || val === PropertyDetailOption.SUPPORTING_PROPERTY,
      then: (schema) => schema.required(),
    })
    .label('Outstanding mortgage'),

  saleStatus: yupMixed<SaleStatusEnum>()
    .oneOf(Object.values(SaleStatus))
    .when('propertyOptions', {
      is: PropertyDetailOption.PROPERTY_TO_SELL,
      then: (schema) => schema.required(),
    })
    .label('Property sold response'),

  saleAmount: yupNumber()
    .when('propertyOptions', {
      is: PropertyDetailOption.PROPERTY_TO_SELL,
      then: (schema) => schema.required(),
    })
    .label('Sale price'),

  depositInTrust: yupBoolean()
    .when(['propertyOptions', 'saleStatus'], ([propertyOptions, saleStatus], schema) =>
      propertyOptions === PropertyDetailOption.PROPERTY_TO_SELL && saleStatus === SaleStatus.SOLD
        ? schema.required()
        : schema,
    )
    .label('Deposit held in trust response'),

  // PROPERTY_TO_PURCHASE fields
  contractForSale: yupBoolean()
    .when(['propertyOptions', 'propertyType'], ([propertyOptions, propertyType], schema) =>
      propertyOptions === PropertyDetailOption.PROPERTY_TO_PURCHASE &&
      !isPropertyPurchaseRestricted(propertyOptions, propertyType)
        ? schema.required()
        : schema,
    )
    .label('Contract for sale response'),

  receiveRentalIncome: yupBoolean()
    .when(
      ['propertyOptions', 'propertyType', 'propertyPurpose'],
      ([propertyOptions, propertyType, propertyPurpose], schema) => {
        if (
          propertyOptions === PropertyDetailOption.PROPERTY_TO_PURCHASE &&
          !isPropertyPurchaseRestricted(propertyOptions, propertyType) &&
          propertyPurpose !== 'owner_occupied'
        ) {
          return schema.required();
        }
        if (
          propertyOptions === PropertyDetailOption.SUPPORTING_PROPERTY &&
          propertyPurpose === PropertyPurpose.INVESTMENT_PROPERTY
        ) {
          return schema.required();
        }
        return schema;
      },
    )
    .label('Rental income response'),

  rentalIncome: yupPeriodicValue(yupNumber().required())
    .when('receiveRentalIncome', {
      is: true,
      then: (schema) => schema.required(),
    })
    .label('Expected rental income'),

  purchaseAmount: yupNumber()
    .when(['propertyOptions', 'propertyType'], ([propertyOptions, propertyType], schema) =>
      propertyOptions === PropertyDetailOption.PROPERTY_TO_PURCHASE &&
      !isPropertyPurchaseRestricted(propertyOptions, propertyType)
        ? schema.required()
        : schema,
    )
    .label('Purchase price'),

  depositType: yupMixed<DepositTypeEnum>()
    .oneOf(Object.values(DepositType))
    .when(['propertyOptions', 'propertyType'], ([propertyOptions, propertyType], schema) =>
      propertyOptions === PropertyDetailOption.PROPERTY_TO_PURCHASE &&
      !isPropertyPurchaseRestricted(propertyOptions, propertyType)
        ? schema.required()
        : schema,
    )
    .label('Deposit payment method'),

  stampDutyStatus: yupMixed<StampDutyStatusEnum>()
    .when(
      ['propertyOptions', 'propertyType', 'contractForSale'],
      ([propertyOptions, propertyType, contractForSale], schema) => {
        if (
          propertyOptions === PropertyDetailOption.PROPERTY_TO_PURCHASE &&
          !isPropertyPurchaseRestricted(propertyOptions, propertyType)
        ) {
          const baseSchema = schema.required().oneOf(Object.values(StampDutyStatus));
          // already_paid is only valid if contract for sale exists
          return contractForSale
            ? baseSchema
            : baseSchema.oneOf(Object.values(StampDutyStatus).filter((item) => item !== StampDutyStatus.ALREADY_PAID));
        }
        return schema;
      },
    )
    .label('Stamp duty response'),

  transferFeeCovered: yupBoolean()
    .when(['propertyOptions', 'propertyType'], ([propertyOptions, propertyType], schema) =>
      propertyOptions === PropertyDetailOption.PROPERTY_TO_PURCHASE &&
      !isPropertyPurchaseRestricted(propertyOptions, propertyType)
        ? schema.required()
        : schema,
    )
    .label('Transfer fee'),

  // Shared field for PROPERTY_TO_SELL and PROPERTY_TO_PURCHASE
  settlementDate: yupString()
    .when(['propertyOptions', 'propertyType', 'saleStatus'], ([propertyOptions, propertyType, saleStatus], schema) => {
      // PROPERTY_TO_SELL: required only when sold
      if (propertyOptions === PropertyDetailOption.PROPERTY_TO_SELL && saleStatus === SaleStatus.SOLD) {
        return schema
          .required()
          .isoDate()
          .test('validDate', '${path} must be within 12 months', (value: string) => {
            const date = dayjs(value);
            return !(date.isBefore(dayjs(), 'day') || date.isAfter(dayjs().add(1, 'year'), 'day'));
          });
      }
      // PROPERTY_TO_PURCHASE: required unless restricted
      if (
        propertyOptions === PropertyDetailOption.PROPERTY_TO_PURCHASE &&
        !isPropertyPurchaseRestricted(propertyOptions, propertyType)
      ) {
        return schema
          .required()
          .isoDate()
          .test('validDate', '${path} must be within 12 months', (value) => {
            const date = dayjs(value);
            return date.isAfter(dayjs(), 'day') && date.isBefore(dayjs().add(1, 'year'), 'day');
          });
      }
      return schema;
    })
    .label('Settlement date'),

  // Shared depositAmount field
  depositAmount: yupNumber()
    .when(
      ['propertyOptions', 'propertyType', 'saleStatus', 'saleAmount', 'purchaseAmount'],
      ([propertyOptions, propertyType, saleStatus, saleAmount, purchaseAmount], schema) => {
        // PROPERTY_TO_SELL: required when sold, max is saleAmount
        if (propertyOptions === PropertyDetailOption.PROPERTY_TO_SELL && saleStatus === SaleStatus.SOLD) {
          return schema.max(saleAmount, 'The deposit cannot be more than the sale price').required();
        }
        // PROPERTY_TO_PURCHASE: required unless restricted, max is purchaseAmount
        if (
          propertyOptions === PropertyDetailOption.PROPERTY_TO_PURCHASE &&
          !isPropertyPurchaseRestricted(propertyOptions, propertyType)
        ) {
          return schema.max(purchaseAmount, 'The deposit cannot be more than the purchase price').required();
        }
        return schema;
      },
    )
    .label('Deposit amount'),

  // SUPPORTING_PROPERTY fields
  supportingPropertyValue: yupNumber()
    .when('propertyOptions', {
      is: PropertyDetailOption.SUPPORTING_PROPERTY,
      then: (schema) => schema.required(),
    })
    .label('Estimated valuation'),
});
